import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import { Panel } from '@components/Panel/Panel'
import { useConnectedNonPrivyWallets } from '@components/Web3/ConnectWallet/useConnectedNonPrivyWallets'
import { AddMiniAppResult } from '@farcaster/miniapp-core/src/actions/AddMiniApp'
import {
    ImpactOccurredType,
    NotificationOccurredType,
} from '@farcaster/miniapp-core/src/actions/Haptics'
import { OpenMiniAppOptions } from '@farcaster/miniapp-core/src/actions/OpenMiniApp'
import { ReadyOptions } from '@farcaster/miniapp-core/src/actions/Ready'
import { SendTokenOptions, SendTokenResult } from '@farcaster/miniapp-core/src/actions/SendToken'
import {
    RejectedByUser as SignInRejectedByUser,
    SignInOptions,
    SignInResult,
} from '@farcaster/miniapp-core/src/actions/SignIn'
import {
    SignManifestOptions,
    SignManifestResult,
} from '@farcaster/miniapp-core/src/actions/SignManifest'
import { SwapTokenOptions, SwapTokenResult } from '@farcaster/miniapp-core/src/actions/SwapToken'
import { ViewCastOptions } from '@farcaster/miniapp-core/src/actions/ViewCast'
import { ViewProfileOptions } from '@farcaster/miniapp-core/src/actions/ViewProfile'
import { ViewTokenOptions } from '@farcaster/miniapp-core/src/actions/ViewToken'
import { BackState } from '@farcaster/miniapp-core/src/back'
import { SetPrimaryButtonOptions } from '@farcaster/miniapp-core/src/types'
import {
    exposeToIframe,
    type HostEndpoint,
    type MiniAppHost,
    type MiniAppHostCapability,
} from '@farcaster/miniapp-host'
import { Box, Paragraph, Stack } from '@ui'
import { getImageUrlFromStreamMetadata } from 'api/lib/fetchImage'
import type { MiniappEmbed } from 'api/lib/unfurl'
import { providers } from 'ethers'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { useDevice } from 'hooks/useDevice'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useMiniappEmbed } from 'hooks/useMiniappEmbed'
import {
    type MiniappSignatureMethod,
    type MiniappSignatureParams,
    useMiniappEthereumProvider,
} from 'hooks/useMiniappEthereumProvider'
import { type GetSigner, WalletReady } from 'privy/WalletReady'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import { type SignatureApprovalRequest, useSignatureApprovalExecutor } from 'signature'
import {
    PrimaryWalletType,
    useBaseWrites,
    useChannelData,
    useMyUserId,
    useMyWallets,
    useSanctionedAddresses,
    useSpaceData,
    useSupportedXChainIds,
    useTownsContext,
    useUser,
} from 'use-towns-client'
import { getPrettyDisplayName } from 'utils/getPrettyDisplayName'
import { Address } from 'viem'

import { useComposerBridge } from '../RichTextPlate/useComposerBridge'

import {
    validateMiniappSignatureRequest,
    validateMiniappTransactionRequest,
} from './ethRequestValidation'
import {
    MiniappSendTokenModal,
    normalizeSendTokenOptions,
    useMiniappSendTokenController,
} from './MiniappSendTokenModal'
import { MiniappSignatureModal, useMiniappSignaturePrompt } from './MiniappSignatureModal'
import { MiniappSplash } from './MiniappSplash'
import { MiniappTransactionModal, useMiniappTransactionPrompt } from './MiniappTransactionModal'
import { buildSignInTypedData, enrichDomainWithChainId } from './singin'

type MiniappPanelData = {
    miniappUrl?: string
    embed?: MiniappEmbed | null
    eventId?: string
}

type LinkedWallet = {
    address: string
    type: PrimaryWalletType
}

const MINIAPP_IFRAME_ALLOW = ['microphone', 'camera', "clipboard-write 'src'"].join('; ') + ';'

const MINIAPP_IFRAME_SANDBOX = 'allow-forms allow-scripts allow-same-origin allow-popups'

const MiniappPanelContent = ({
    getSigner,
    panelData,
}: {
    getSigner: GetSigner
    panelData: MiniappPanelData
}) => {
    const iframeRef = useRef<HTMLIFrameElement | null>(null)
    const splashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const endpointRef = useRef<HostEndpoint | null>(null)
    const cleanupRef = useRef<(() => void) | null>(null)
    const userId = useMyUserId()
    const user = useUser(userId)
    const { enableMiniapps } = useFeatureFlags()
    const { closePanel } = usePanelActions()
    const { data: abstractAccountAddress } = useMyAbstractAccountAddress()
    const { environmentId, baseChain } = useTownsContext()
    const { spaceId, channelId, channel } = useChannelData()
    const spaceData = useSpaceData()
    const { data: walletAddresses } = useMyWallets()
    const supportedChainIds = useSupportedXChainIds()
    const { isSanctioned } = useSanctionedAddresses()
    const { wallets: connectedWallets } = useConnectedNonPrivyWallets()
    const { userOpsInstance } = useBaseWrites()
    const { executeSignature } = useSignatureApprovalExecutor({
        connectedWallets,
        userOpsInstance,
    })

    const linkedWallets = useMemo<LinkedWallet[]>(() => {
        const seen = new Set<string>()
        const aaLower = abstractAccountAddress?.toLowerCase()
        const walletsWithType: LinkedWallet[] = []

        walletAddresses?.forEach((address) => {
            if (!address) {
                return
            }
            const lower = address.toLowerCase()
            if (seen.has(lower)) {
                return
            }
            seen.add(lower)
            const type: PrimaryWalletType = aaLower && lower === aaLower ? 'smartAccount' : 'eoa'
            walletsWithType.push({
                address,
                type,
            })
        })

        if (aaLower && !seen.has(aaLower) && abstractAccountAddress) {
            seen.add(aaLower)
            walletsWithType.push({
                address: abstractAccountAddress,
                type: 'smartAccount',
            })
        }

        return walletsWithType
    }, [walletAddresses, abstractAccountAddress])

    const {
        prompt: transactionPrompt,
        requestTransactionApproval,
        handleTransactionApprove,
        handleTransactionReject,
    } = useMiniappTransactionPrompt()
    const {
        prompt: signaturePrompt,
        requestSignatureApproval: openSignaturePrompt,
        resolveSignaturePrompt,
        handleSignatureReject,
    } = useMiniappSignaturePrompt()
    const {
        provider: ethProvider,
        privyAddress,
        loadProviderForAddress,
    } = useMiniappEthereumProvider({
        onTransactionRequest: async ({ tx, method }) => {
            if (method === 'eth_sendTransaction') {
                if (!abstractAccountAddress) {
                    throw new Error('Smart account address is unavailable')
                }
                validateMiniappTransactionRequest(tx, abstractAccountAddress)
            } else if (method === 'eth_sendRawTransaction') {
                if (!privyAddress) {
                    throw new Error('Privy wallet address is unavailable')
                }
                validateMiniappTransactionRequest(tx, privyAddress)
            }

            return requestTransactionApproval({ tx, method })
        },
        onSignatureRequest: async ({ method, params, fromAddress }) => {
            if (!abstractAccountAddress) {
                throw new Error('Smart account address is unavailable')
            }
            if (!baseChain) {
                throw new Error('Base chain configuration is unavailable')
            }

            validateMiniappSignatureRequest(
                {
                    method,
                    params,
                    fromAddress,
                },
                abstractAccountAddress,
            )

            await openSignaturePrompt({
                method,
                params,
                fromAddress,
            })

            const signatureRequest = buildMiniappSignatureRequest({
                method,
                params,
                chainId: baseChain.id,
                signerWallet: abstractAccountAddress as Address,
            })

            return executeSignature({
                selectedWallet: abstractAccountAddress as Address,
                abstractAccountAddress: abstractAccountAddress as Address,
                request: signatureRequest,
                getRootKeySigner: getSigner,
            })
        },
    })

    const {
        prompt: sendTokenPrompt,
        requestSendTokenApproval,
        isProcessing: isSendingToken,
        handleCancel: handleSendTokenCancel,
        handleApprove: handleSendTokenApprove,
    } = useMiniappSendTokenController({
        loadProviderForAddress,
        privyAddress,
    })

    const [miniappEmbed, setMiniappEmbed] = useState<MiniappEmbed | null>(
        () => panelData.embed ?? null,
    )
    const [isSplashVisible, setIsSplashVisible] = useState(true)
    const [isIframeLoaded, setIsIframeLoaded] = useState(false)
    const [iframeLoadError, setIframeLoadError] = useState<string | null>(null)
    const [iframeReloadCount, setIframeReloadCount] = useState(0)
    const [isSplashFallbackActive, setIsSplashFallbackActive] = useState(false)
    const shouldFetchEmbedUrl =
        !panelData.embed && panelData.miniappUrl ? panelData.miniappUrl : null
    const { embed: fetchedMiniappEmbed, isLoading: isMiniappEmbedFetching } =
        useMiniappEmbed(shouldFetchEmbedUrl)
    const isMiniappEmbedLoading = !!shouldFetchEmbedUrl && isMiniappEmbedFetching

    const clearSplashTimeout = useCallback(() => {
        if (splashTimeoutRef.current) {
            clearTimeout(splashTimeoutRef.current)
            splashTimeoutRef.current = null
        }
    }, [])

    const dismissSplash = useCallback(() => {
        setIsSplashVisible(false)
        setIsSplashFallbackActive(false)
        clearSplashTimeout()
    }, [clearSplashTimeout])

    // Keep embed state in sync with provided or fetched data
    useEffect(() => {
        if (panelData.embed) {
            setMiniappEmbed(panelData.embed)
            return
        }

        if (!panelData.miniappUrl) {
            setMiniappEmbed(null)
            return
        }

        setMiniappEmbed(fetchedMiniappEmbed ?? null)
    }, [panelData.embed, panelData.miniappUrl, fetchedMiniappEmbed])

    // Reset state when URL changes or iframe reloads
    useEffect(() => {
        setIsSplashVisible(true)
        setIsIframeLoaded(false)
        setIframeLoadError(null)
        setIsSplashFallbackActive(false)
        clearSplashTimeout()
        return () => clearSplashTimeout()
    }, [panelData.miniappUrl, iframeReloadCount, clearSplashTimeout])

    const handleSplashRetry = useCallback(() => {
        setIframeReloadCount((count) => count + 1)
    }, [])

    const panelTitle =
        miniappEmbed?.button?.action?.name?.trim() ||
        miniappEmbed?.button?.title?.trim() ||
        'Miniapp'

    const miniappOrigin = useMemo(() => {
        try {
            return panelData.miniappUrl ? new URL(panelData.miniappUrl).origin : undefined
        } catch {
            return undefined
        }
    }, [panelData.miniappUrl])

    const composerBridge = useComposerBridge()
    const { isTouch } = useDevice()

    // Callback to insert text into editor
    const insertTextIntoEditor = useCallback(
        (text: string, embeds?: string[]) => {
            if (!composerBridge) {
                console.warn('[MiniappPanel] composeCast requested but composer is unavailable')
                return
            }
            composerBridge.insertText(text, embeds)
        },
        [composerBridge],
    )
    const sdk = useMemo(() => {
        if (!user || !abstractAccountAddress || !environmentId || !baseChain) {
            console.log('[MiniappPanel:SDK] SDK not ready, missing dependencies:', {
                hasUser: !!user,
                hasAbstractAccountAddress: !!abstractAccountAddress,
                hasEnvironmentId: !!environmentId,
                hasBaseChain: !!baseChain,
            })
            return null
        }
        console.log('[MiniappPanel:SDK] SDK created with context')
        return {
            context: {
                client: {
                    platformType: isTouch ? 'mobile' : 'web',
                    clientFid: 0,
                    added: false,
                    safeAreaInsets: {
                        top: 0,
                        bottom: 0,
                        left: 0,
                        right: 0,
                    },
                },
                user: {
                    fid: 0,
                    displayName: getPrettyDisplayName(user),
                    pfpUrl: getImageUrlFromStreamMetadata(user.userId),
                    username: user.username,
                },
                features: {
                    haptics: false,
                    cameraAndMicrophoneAccess: false,
                },
                towns: {
                    user: {
                        userId: user.userId,
                        linkedWallets,
                    },
                    env: environmentId,
                    spaceId,
                    spaceName: spaceData?.name,
                    channelId,
                    channelName: channel?.label,
                    messageId: panelData.eventId,
                },
            },

            close: (): void => {
                // Closes the Mini App
                console.log('[MiniappPanel:SDK] 🚪 close() called')
                closePanel({ preventPopStack: true })
            },
            ready: async (options?: Partial<ReadyOptions>) => {
                // Hides the Splash Screen
                console.log('[MiniappPanel:SDK] ✅ ready() called', { options })
                dismissSplash()
            },
            openUrl: (url: string): void => {
                // Open an external URL
                console.log('[MiniappPanel:SDK] 🔗 openUrl() called:', url)
                if (typeof window !== 'undefined') {
                    window.open(url, '_blank', 'noopener,noreferrer')
                }
            },
            signIn: async (options: SignInOptions): Promise<SignInResult> => {
                console.log('[MiniappPanel:SDK] 🔐 signIn() called', { options })
                if (!privyAddress) {
                    console.error('[MiniappPanel:SDK] ❌ No Privy wallet available for sign-in')
                    throw new Error('No Privy wallet available for sign-in')
                }

                try {
                    const privyProvider = await loadProviderForAddress(privyAddress)
                    const typedData = buildSignInTypedData(options)
                    const web3Provider = new providers.Web3Provider(
                        privyProvider as providers.ExternalProvider,
                    )
                    const signer = await web3Provider.getSigner(privyAddress)
                    const domainWithChainId = await enrichDomainWithChainId(
                        signer,
                        typedData.domain,
                    )
                    const signature = await signer._signTypedData(
                        domainWithChainId,
                        typedData.types,
                        typedData.value,
                    )
                    console.log('[MiniappPanel:SDK] 🔐 signIn() success')
                    return {
                        signature,
                        message: JSON.stringify({
                            domain: domainWithChainId,
                            types: typedData.types,
                            value: typedData.value,
                        }),
                        authMethod: 'custody' as const,
                    }
                } catch (error) {
                    console.error('[MiniappPanel:SDK] ❌ signIn() failed', error)
                    if (isUserRejectedError(error)) {
                        throw new SignInRejectedByUser()
                    }
                    throw error instanceof Error ? error : new Error('Wallet sign-in failed')
                }
            },
            signManifest: async (options: SignManifestOptions): Promise<SignManifestResult> => {
                // Lets a Miniapp ask the Towns wallet client to cryptographically sign a
                // “manifest” that describes the given miniapp.
                console.log('[MiniappPanel:SDK] 📜 signManifest() called', { options })
                throw new Error('experimental.signManifest is not supported')
            },
            setPrimaryButton: (options: SetPrimaryButtonOptions): void => {
                // There is a primary call-to-action button hosted on the client UI outside of iframe.
                // This button can be enabled/hidden/named by calling this method.
                // There is sdk.on("primaryButtonClick", () => {}) hook to listen for clicks.
                console.log('[MiniappPanel:SDK] 🔘 setPrimaryButton() called', { options })
            },
            ethProviderRequest: async (request) => {
                // THIS HOOK IS NOT USED. CHECK ExposeToEndpoint.
                throw new Error('Unexpected ethProviderRequest call')
            },
            eip6963RequestProvider: () => {
                // The hook the miniapp/frame bridge uses to discover and select an injected EIP-6963 wallet provider
                // (or providers) and then turn that into an EIP-1193 provider the miniapp can use:
                // "Ask the host environment to run the EIP-6963 discovery flow and give me a wallet provider I can talk to."
                // - Untrusted providers can announce themselves
                // - Never silently pick the first wallet that announces.
                // - Don’t share the provider object across apps
                // - Don’t log sensitive data
                // - Handle race conditions and spoofing attempts
                console.log('[MiniappPanel:SDK] 🔌 eip6963RequestProvider() called', {
                    hasEndpoint: !!endpointRef.current,
                    hasEthProvider: !!ethProvider,
                })
                if (endpointRef.current && ethProvider) {
                    endpointRef.current.emit({
                        event: 'eip6963:announceProvider',
                        info: {
                            uuid: crypto.randomUUID(),
                            name: 'Towns',
                            icon: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"%3E%3Ctext y="20" font-size="20"%3E%F0%9F%8C%90%3C/text%3E%3C/svg%3E',
                            rdns: 'com.towns.miniapp',
                        },
                    })
                    console.log('[MiniappPanel:SDK] ✅ Announced provider to endpoint')
                }
            },

            solanaProviderRequest: async (request: Record<string, unknown>) => {
                // the Solana analog of ethProviderRequest: it’s the hook the miniapp bridge uses to forward Solana
                // wallet / JSON-RPC style requests from the miniapp to a real Solana wallet/provider in the host,
                // and then return the result back.
                console.log('[MiniappPanel:SDK] solanaProviderRequest() called - not supported', {
                    request,
                })
                throw new Error('wallet.solanaProviderRequest is not supported')
            },
            addMiniApp: async (): Promise<AddMiniAppResult> => {
                // Prompts the user to add the app. This is not relevant to Towns miniapps.
                console.log('[MiniappPanel:SDK] addMiniApp() called - not supported')
                throw new Error('actions.addMiniApp is not supported')
            },
            viewCast: async (options: ViewCastOptions) => {
                // This navigates the user to view the full cast with its replies and reactions.
                // Should we use this hook to navigate to the thread/message in Towns?
                console.log('[MiniappPanel:SDK] viewCast() called - not supported', { options })
                throw new Error('actions.viewCast is not supported')
            },
            viewProfile: async (options: ViewProfileOptions) => {
                // It allows a miniapp to ask the host client (Towns app) to open a user’s Towns profile view natively:
                // “Hey host app, open the profile page for FID X.”
                console.log('[MiniappPanel:SDK] viewProfile() called - not supported', { options })
                throw new Error('actions.viewProfile is not supported')
            },
            viewToken: async (options: ViewTokenOptions) => {
                // It lets a miniapp ask the host client (Towns app) to open the native token view
                // for a specific on-chain asset:
                // “Open the token page in the host app for this contract on this chain.”
                console.log('[MiniappPanel:SDK] viewToken() called - not supported', { options })
                throw new Error('actions.viewToken is not supported')
            },
            sendToken: async (options: SendTokenOptions): Promise<SendTokenResult> => {
                console.log('[MiniappPanel:SDK] sendToken() called', { options })
                const normalized = normalizeSendTokenOptions({
                    options,
                    supportedChainIds,
                    nativeSymbol: baseChain.nativeCurrency?.symbol ?? 'ETH',
                    nativeDecimals: baseChain.nativeCurrency?.decimals ?? 18,
                    resolveChainName: (chainId) =>
                        chainId === baseChain.id ? baseChain.name : `Chain ${chainId}`,
                })

                if (isSanctioned(normalized.recipientAddress)) {
                    throw new Error('Recipient address is blocked')
                }

                return requestSendTokenApproval({
                    ...normalized,
                    fromAddress: abstractAccountAddress,
                })
            },
            swapToken: async (options: SwapTokenOptions): Promise<SwapTokenResult> => {
                // It allows a miniapp to request that the host wallet open its native swap UI
                // with pre-filled parameters (token in, token out, amount, chain):
                // “Open the host’s swap screen so the user can swap Token A → Token B with the amount I specify.”
                console.log('[MiniappPanel:SDK] swapToken() called - not supported', { options })
                throw new Error('actions.swapToken is not supported')
            },
            openMiniApp: async (options: OpenMiniAppOptions) => {
                // Lets a miniapp ask the host client (Towns app) to open another miniapp:
                // “Open this other miniapp inside the host, optionally passing it some initial parameters.”
                console.log('[MiniappPanel:SDK] openMiniApp() called - not supported', { options })
                throw new Error('actions.openMiniApp is not supported')
            },
            composeCast: async (options) => {
                // It lets a miniapp ask the host client (Towns app) to open the native message composer
                // with fields pre-filled — text, embeds, mentions, links, or even structured embeds:
                // “Open the ‘New Message’ (New Cast in Farcaster) screen with these contents already filled in.”
                console.log('[MiniappPanel:SDK] composeCast() called', { options })
                if (options.text || options.embeds) {
                    insertTextIntoEditor(options.text ?? '', options.embeds ?? [])
                }
                if (options?.close) {
                    return closePanel({ preventPopStack: true })
                }
                // TODO: we're not letting miniapps know if the message was posted or not
                return { cast: null }
            },
            requestCameraAndMicrophoneAccess: async () => {
                // Allows a miniapp to ask the host app (Towns app) for access to the device camera and microphone,
                // similar to WebRTC’s getUserMedia() — but with host-controlled permission and UI.
                console.log(
                    '[MiniappPanel:SDK] requestCameraAndMicrophoneAccess() called - not supported',
                )
                throw new Error('actions.requestCameraAndMicrophoneAccess is not supported')
            },

            impactOccurred: async (type: ImpactOccurredType) => {
                // It lets a miniapp trigger native vibration / haptic impact on the user’s device
                // through the host client (Towns app):
                // “Tell the host app to fire a short haptic bump / tap / vibration.”
                console.log('[MiniappPanel:SDK] impactOccurred() called', { type })
            },

            notificationOccurred: async (type: NotificationOccurredType) => {
                // While impactOccurred is for short tactile “taps,”
                // notificationOccurred is the system-level haptic pattern used for success, warning, and error notifs.
                // It maps directly to iOS’s UINotificationFeedbackGenerator and Android’s
                // equivalent notification haptic patterns:
                // “Trigger a notification-style haptic vibration: success, warning, or error.”
                console.log('[MiniappPanel:SDK] notificationOccurred() called', { type })
            },

            selectionChanged: async () => {
                // It triggers a “selection change” haptic, which is a soft, subtle, low-intensity vibration meant for:
                // - scrolling through options
                // - changing a picker value
                // - switching tabs
                // - slider adjustments
                // - toggling UI states
                // It’s the equivalent of iOS’s UISelectionFeedbackGenerator().selectionChanged().
                console.log('[MiniappPanel:SDK] selectionChanged() called')
            },

            getCapabilities: async (): Promise<MiniAppHostCapability[]> => {
                // “Which features, chains, wallets, and permissions do you (the host) support?”
                const capabilities = [
                    'wallet.getEthereumProvider',
                    'actions.ready',
                    'actions.openUrl',
                    'actions.close',
                    'actions.sendToken',
                    'actions.composeCast',
                    'back',
                ] satisfies MiniAppHostCapability[]
                console.log(
                    '[MiniappPanel:SDK] 🎯 getCapabilities() called, returning:',
                    capabilities,
                )
                return capabilities
            },
            getChains: async (): Promise<string[]> => {
                const chainStrings = supportedChainIds.map((chainId) => chainId.toString())
                console.log('[MiniappPanel:SDK] ⛓️ getChains() called', {
                    chains: chainStrings,
                })
                return chainStrings
            },
            updateBackState: async (state: BackState) => {
                // It lets a miniapp tell the host app (Towns app):
                // “Here is my current navigation state.
                // If the user presses the host’s Back button, call my handler instead of closing the miniapp.”
                console.log('[MiniappPanel:SDK] updateBackState() called', { state })
            },
        } as Omit<MiniAppHost, 'ethProviderRequestV2' | 'addFrame'>
    }, [
        user,
        abstractAccountAddress,
        environmentId,
        baseChain,
        isTouch,
        spaceId,
        channelId,
        spaceData,
        channel,
        panelData.eventId,
        closePanel,
        ethProvider,
        insertTextIntoEditor,
        linkedWallets,
        supportedChainIds,
        privyAddress,
        loadProviderForAddress,
        requestSendTokenApproval,
        isSanctioned,
        dismissSplash,
    ])

    const iframeKey = `${panelData.miniappUrl ?? 'miniapp'}-${iframeReloadCount}`

    const scheduleSplashFallback = useCallback(() => {
        clearSplashTimeout()
        splashTimeoutRef.current = setTimeout(() => {
            setIsSplashFallbackActive(true)
        }, 5_000)
    }, [clearSplashTimeout])

    const attachSdkToIframe = useCallback(() => {
        const iframe = iframeRef.current
        if (!iframe || !miniappOrigin || !sdk) {
            return
        }

        try {
            const { endpoint, cleanup: sdkCleanup } = exposeToIframe({
                iframe,
                sdk: sdk as Omit<MiniAppHost, 'ethProviderRequestV2'>,
                miniAppOrigin: miniappOrigin,
                ethProvider: ethProvider ?? undefined,
                debug: true,
            })
            endpointRef.current = endpoint

            const handleLoad = () => {
                setIframeLoadError(null)
                setIsIframeLoaded(true)
                scheduleSplashFallback()
            }
            const handleError = () => {
                setIsIframeLoaded(false)
                setIframeLoadError('Unable to load this miniapp. Please try again.')
                clearSplashTimeout()
            }

            iframe.addEventListener('load', handleLoad)
            iframe.addEventListener('error', handleError)

            cleanupRef.current = () => {
                iframe.removeEventListener('load', handleLoad)
                iframe.removeEventListener('error', handleError)
                sdkCleanup()
            }
        } catch (error) {
            console.error('[MiniappPanel] Error exposing SDK:', error)
        }
    }, [miniappOrigin, sdk, ethProvider, scheduleSplashFallback, clearSplashTimeout])

    const handleIframeRef = useCallback(
        (el: HTMLIFrameElement | null) => {
            cleanupRef.current?.()
            cleanupRef.current = null
            endpointRef.current = null
            iframeRef.current = el

            if (!el) {
                return
            }

            attachSdkToIframe()
        },
        [attachSdkToIframe],
    )

    useEffect(() => {
        if (!iframeRef.current) {
            return
        }
        cleanupRef.current?.()
        cleanupRef.current = null
        endpointRef.current = null
        attachSdkToIframe()
    }, [attachSdkToIframe])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanupRef.current?.()
            cleanupRef.current = null
            endpointRef.current = null
        }
    }, [])

    return (
        <Panel label={panelTitle} padding="none">
            <Stack height="100%">
                <Box
                    overflow="hidden"
                    border="faint"
                    width="100%"
                    height="100%"
                    position="relative"
                >
                    {enableMiniapps && sdk ? (
                        <iframe
                            key={iframeKey}
                            ref={handleIframeRef}
                            title={`Miniapp: ${panelTitle}`}
                            src={panelData.miniappUrl}
                            allow={MINIAPP_IFRAME_ALLOW}
                            sandbox={MINIAPP_IFRAME_SANDBOX}
                            referrerPolicy="strict-origin-when-cross-origin"
                            loading="eager"
                            style={{
                                width: '100%',
                                height: '100%',
                                border: '0',
                                backgroundColor: 'transparent',
                            }}
                        />
                    ) : !enableMiniapps ? (
                        <Box padding="lg">
                            <Paragraph>Miniapps are not enabled for this environment</Paragraph>
                        </Box>
                    ) : null}
                    {enableMiniapps && isSplashVisible && (
                        <MiniappSplash
                            embed={miniappEmbed}
                            miniappUrl={panelData.miniappUrl}
                            state={{
                                missingUrl: !panelData.miniappUrl,
                                invalidOrigin: !!panelData.miniappUrl && !miniappOrigin,
                                iframeError: iframeLoadError,
                                iframeLoaded: isIframeLoaded,
                                embedLoading: isMiniappEmbedLoading,
                                fallbackActive: isSplashFallbackActive,
                                sdkLoading: !sdk,
                            }}
                            onFallbackContinue={dismissSplash}
                            onRetry={handleSplashRetry}
                        />
                    )}
                </Box>
            </Stack>
            <MiniappTransactionModal
                prompt={transactionPrompt}
                onApprove={handleTransactionApprove}
                onReject={handleTransactionReject}
            />
            <MiniappSignatureModal
                prompt={signaturePrompt}
                onApprove={resolveSignaturePrompt}
                onReject={handleSignatureReject}
            />
            <MiniappSendTokenModal
                prompt={sendTokenPrompt}
                isProcessing={isSendingToken}
                onCancel={handleSendTokenCancel}
                onConfirm={handleSendTokenApprove}
            />
        </Panel>
    )
}

const isUserRejectedError = (error: unknown) => {
    if (!error || typeof error !== 'object') {
        return false
    }
    const code = (error as { code?: number | string }).code
    return code === 4001 || code === 'ACTION_REJECTED'
}

type MiniappSignatureRequestArgs = {
    method: MiniappSignatureMethod
    params: MiniappSignatureParams
    chainId: number
    signerWallet: Address
}

const buildMiniappSignatureRequest = ({
    method,
    params,
    chainId,
    signerWallet,
}: MiniappSignatureRequestArgs): SignatureApprovalRequest => {
    const normalizedParams = (Array.isArray(params) ? params : []) as unknown[]
    const baseRequest = {
        chainId,
        signerWallet,
        allowEIP1271: true,
    }

    switch (method) {
        case 'personal_sign': {
            return {
                ...baseRequest,
                type: 'personal',
                payload: normalizedParams[0] as string,
            }
        }
        case 'eth_sign': {
            return {
                ...baseRequest,
                type: 'personal',
                payload: normalizedParams[1] as string,
            }
        }
        case 'eth_signTypedData':
        case 'eth_signTypedData_v4': {
            let payload: string
            if (typeof normalizedParams[1] === 'string') {
                payload = normalizedParams[1]
            } else if (typeof normalizedParams[1] === 'object') {
                payload = JSON.stringify(normalizedParams[1])
            } else {
                throw new Error('Invalid params for eth_signTypedData')
            }
            return {
                ...baseRequest,
                type: 'typed',
                payload: payload,
            }
        }
        default:
            throw new Error(`Unsupported signature method: ${method}`)
    }
}

export const MiniappPanel = () => {
    const [searchParams] = useSearchParams()
    const dataParam = searchParams.get('data')
    const panelData: MiniappPanelData = useMemo(() => {
        if (!dataParam) {
            return {
                miniappUrl: searchParams.get('miniappUrl') ?? undefined,
                embed: null,
                eventId: searchParams.get('eventId') ?? undefined,
            }
        }

        try {
            const parsed = JSON.parse(dataParam) as MiniappPanelData
            return {
                miniappUrl: parsed.miniappUrl ?? undefined,
                embed: parsed.embed ?? null,
                eventId: parsed.eventId ?? undefined,
            }
        } catch (error) {
            console.warn(
                '[MiniappPanel] Failed to parse panel data JSON, falling back to query params',
                {
                    dataParam,
                    error,
                },
            )
            return {
                miniappUrl: searchParams.get('miniappUrl') ?? undefined,
                embed: null,
                eventId: searchParams.get('eventId') ?? undefined,
            }
        }
    }, [dataParam, searchParams])

    return (
        <WalletReady>
            {({ getSigner }) => <MiniappPanelContent getSigner={getSigner} panelData={panelData} />}
        </WalletReady>
    )
}
